import React, { useState } from 'react';
import {
  Button,
  Typography,
  Space,
  Select,
  Card,
  Tooltip,
  Tag,
  Alert,
  Divider,
} from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  EditOutlined,
  FontSizeOutlined,
  MailOutlined,
  UserOutlined,
  CalendarOutlined,
  FormOutlined,
} from '@ant-design/icons';
import { FIELD_TYPES, RECIPIENT_COLORS } from '../DocumentWorkflow';

const { Title, Text } = Typography;
const { Option } = Select;

// Field type icons
const FIELD_ICONS = {
  SIGNATURE: <EditOutlined />,
  TEXT: <FontSizeOutlined />,
  EMAIL: <MailOutlined />,
  NAME: <UserOutlined />,
  DATE: <CalendarOutlined />,
  INITIALS: <FormOutlined />,
};

const StepAddFields = ({
  formData,
  onNext,
  onBack,
  onSubmit,
  fieldsByRecipient,
  setFieldsByRecipient,
  onActiveRecipientSelect,
  activeRecipient,
}) => {
  const [loading, setLoading] = useState(false);
  const [, setDraggedFieldType] = useState(null);

  const recipients = formData.recipients || [];

  // Get recipient by ID
  const getRecipientById = (id) => {
    return recipients.find((r) => r.id === id || r.key === id);
  };

  // Get recipient color
  const getRecipientColor = (id) => {
    const recipient = getRecipientById(id);
    if (recipient?.color) return recipient.color;
    const index = recipients.findIndex((r) => r.id === id || r.key === id);
    return RECIPIENT_COLORS[index % RECIPIENT_COLORS.length];
  };

  // Count fields for a recipient
  const getFieldCount = (recipientId) => {
    return (fieldsByRecipient[recipientId] || []).length;
  };

  // Get total field count
  const getTotalFieldCount = () => {
    return Object.values(fieldsByRecipient).flat().length;
  };

  // Handle drag start for field type
  const handleDragStart = (e, fieldType) => {
    if (!activeRecipient) {
      e.preventDefault();
      return;
    }
    setDraggedFieldType(fieldType);
    e.dataTransfer.setData('fieldType', fieldType);
    e.dataTransfer.setData('recipientId', activeRecipient);
  };

  // Handle recipient selection
  const handleRecipientSelect = (value) => {
    onActiveRecipientSelect(value);
  };

  // Handle submit
  const handleFinish = async () => {
    if (getTotalFieldCount() === 0) {
      // Allow proceeding without fields for now
    }

    setLoading(true);
    try {
      await onSubmit(3, {});
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Title level={4}>Add Fields</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Select a recipient, then drag fields onto the document.
      </Text>

      {/* Recipient Selector */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Select Recipient
        </Text>
        <Select
          placeholder="Select a recipient to add fields for"
          value={activeRecipient}
          onChange={handleRecipientSelect}
          style={{ width: '100%' }}
          size="large"
        >
          {recipients.map((recipient, index) => (
            <Option key={recipient.id || recipient.key} value={recipient.id || recipient.key}>
              <Space>
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: recipient.color || RECIPIENT_COLORS[index % RECIPIENT_COLORS.length],
                  }}
                />
                <span>{recipient.name}</span>
                <Tag size="small">{recipient.recipient_role}</Tag>
                {getFieldCount(recipient.id || recipient.key) > 0 && (
                  <Tag color="blue">{getFieldCount(recipient.id || recipient.key)} fields</Tag>
                )}
              </Space>
            </Option>
          ))}
        </Select>
      </Card>

      {/* Field Types Palette */}
      {activeRecipient ? (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 12 }}>
            Drag fields onto the document
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {FIELD_TYPES.map((field) => (
              <Tooltip key={field.type} title={`Drag to add ${field.label} field`}>
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, field.type)}
                  style={{
                    padding: '8px 12px',
                    border: `2px solid ${getRecipientColor(activeRecipient)}`,
                    borderRadius: 6,
                    cursor: 'grab',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    backgroundColor: '#fff',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  {FIELD_ICONS[field.type]}
                  <span style={{ fontSize: 13 }}>{field.label}</span>
                </div>
              </Tooltip>
            ))}
          </div>
        </Card>
      ) : (
        <Alert
          message="Select a recipient first"
          description="Choose a recipient above to start adding fields for them."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Field Summary */}
      <Divider style={{ margin: '16px 0' }} />
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        Field Summary
      </Text>
      {recipients.map((recipient, index) => {
        const fieldCount = getFieldCount(recipient.id || recipient.key);
        const fields = fieldsByRecipient[recipient.id || recipient.key] || [];
        return (
          <div
            key={recipient.id || recipient.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            <Space>
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: recipient.color || RECIPIENT_COLORS[index % RECIPIENT_COLORS.length],
                }}
              />
              <Text>{recipient.name}</Text>
            </Space>
            <Space>
              {fieldCount > 0 ? (
                <>
                  {fields.map((f) => (
                    <Tag key={f.id} size="small">
                      {f.type}
                    </Tag>
                  ))}
                </>
              ) : (
                <Text type="secondary">No fields</Text>
              )}
            </Space>
          </div>
        );
      })}

      {/* Navigation Buttons */}
      <Space style={{ marginTop: 24 }}>
        <Button onClick={onBack} icon={<ArrowLeftOutlined />} size="large">
          Back
        </Button>
        <Button
          type="primary"
          onClick={handleFinish}
          loading={loading}
          icon={<ArrowRightOutlined />}
          size="large"
        >
          Next: Distribution
        </Button>
      </Space>
    </div>
  );
};

export default StepAddFields;
